/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRoles,
  createRole,
  updateRole,
  clearRoleError,
} from "../store/slices/roleSlice";
import Layout from "../common/components/Layout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Roles = () => {
  const dispatch = useDispatch();
  const {
    list: roles = [],
    status,
    error,
  } = useSelector((state) => state.role || {});

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    reportsToId: null,
    isActive: true,
    assignmentType: "Both",
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "title",
    direction: "asc",
  });

  const companyId = localStorage.getItem("companyId") || null;

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  // useEffect(() => {
  //   if (error) toast.error(error);
  // }, [error]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return (
        <i
          className="bi bi-arrow-down-up ms-2 opacity-25"
          style={{ fontSize: "0.6rem" }}
        ></i>
      );
    return sortConfig.direction === "asc" ? (
      <i
        className="bi bi-sort-down ms-2 text-primary"
        style={{ fontSize: "0.7rem" }}
      ></i>
    ) : (
      <i
        className="bi bi-sort-up ms-2 text-primary"
        style={{ fontSize: "0.7rem" }}
      ></i>
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    dispatch(clearRoleError());
    setIsEditMode(false);
    setFormData({
      id: "",
      title: "",
      description: "",
      reportsToId: null,
      isActive: true,
      assignmentType: "Both",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (role) => {
    dispatch(clearRoleError());
    setIsEditMode(true);
    setFormData({
      id: role.id,
      title: role.title,
      description: role.description || "",
      reportsToId: role.reportsToId
 || null,
      assignmentType: role.assignmentType,
      isActive: role.isActive ?? true,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title,
      description: formData.description,
      isActive: formData.isActive,
      assignmentType: formData.assignmentType || "Both",
      reportsToId: formData.reportsToId || null,
      companyId,
    };

    try {
      const action = isEditMode
        ? updateRole({ id: formData.id, data: payload })
        : createRole(payload);

      await dispatch(action).unwrap();

      toast.success(`"${formData.title}" saved successfully!`);
      setShowModal(false);
      dispatch(fetchRoles());
    } catch (err) {
      console.error("Role Save Error:", err);
      const errorMessage = err?.message || err || "Failed to save role.";

      toast.error(errorMessage);
    }
  };

  const handleStatusToggle = (role) => {
    if (role.isActive) {
      setPendingRole(role);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(role);
    }
  };

  const executeStatusUpdate = async (role) => {
    const payload = {
      title: role.title,
      description: role.description || "",
      isActive: !role.isActive,
      reportsToId: role.reportsTo?.id || null,
      assignmentType: formData.assignmentType || "Both",
      companyId,
    };

    try {
      await dispatch(updateRole({ id: role.id, data: payload })).unwrap();
      toast.success(`${role.title} status updated!`);
      setShowConfirm(false);
      dispatch(fetchRoles());
    } catch (err) {
      toast.error("Could not change status.");
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(roles) ? [...roles] : [];
    items = items.filter((item) => {
      const matchesSearch =
        (item.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "All" ||
        (item.isActive ? "Active" : "Inactive") === statusFilter;
      return matchesSearch && matchesStatus;
    });

    return items.sort((a, b) => {
      let valA, valB;
      if (sortConfig.key === "reportsTo") {
        valA = a.reportsTo?.title?.toLowerCase() || "";
        valB = b.reportsTo?.title?.toLowerCase() || "";
      } else {
        valA = (a[sortConfig.key] || "").toString().toLowerCase();
        valB = (b[sortConfig.key] || "").toString().toLowerCase();
      }
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [roles, searchTerm, statusFilter, sortConfig]);

  const totalItems = processedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getPaginationGroup = () => {
    const totalDisplayed = 5;
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages;
  };

  const inputStyle = {
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "0.8rem",
    backgroundColor: "rgba(255,255,255,0.7)",
    backdropFilter: "blur(5px)",
    outline: "none",
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container-fluid py-4 flex-grow-1 px-lg-5 w-100">
        <style>{`
          .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; cursor: pointer; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
          .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; }
          .page-link-custom.active { background: #0f172a !important; color: #fff !important; }
          .page-link-custom.disabled { opacity: 0.3; cursor: not-allowed; }
          .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        `}</style>

        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1
              className="fw-bold text-dark m-0"
              style={{ letterSpacing: "-1.5px" }}
            >
              Organization Roles
            </h1>
            <p className="text-muted small mb-0">
              Define hierarchy and reporting structures
            </p>
          </div>
          <button
            className="btn btn-primary px-4 border-0"
            style={{
              borderRadius: "8px",
              backgroundColor: "#0f172a",
              fontWeight: "600",
              fontSize: "0.85rem",
            }}
            onClick={handleOpenAdd}
          >
            <i className="bi bi-plus-lg me-2"></i>Add New
          </button>
        </div>

        {/* Filters Row */}
        <div className="row g-2 mb-4">
          <div className="col-md-9">
            <input
              type="text"
              className="form-control"
              placeholder="Search roles by title or description..."
              style={inputStyle}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              style={inputStyle}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Glass Table Container */}

        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  {[
                    { label: "ROLE TITLE", key: "title" },
                    { label: "DESCRIPTION", key: null },
                    { label: "REPORTS TO", key: "reportsTo" },
                    { label: "ASSIGNMENT TYPE", key: "assignmentType" },
                    { label: "STATUS", key: null },
                    { label: "ACTIONS", key: null },
                  ].map((col, idx) => (
                    <th
                      key={idx}
                      className={`${idx === 0 ? "ps-4" : ""} py-3 border-0 ${col.label === "STATUS" ? "text-center" : ""} ${col.label === "ACTIONS" ? "text-end pe-4" : ""}`}
                      style={{ cursor: col.key ? "pointer" : "default" }}
                      onClick={() => col.key && requestSort(col.key)}
                    >
                      {col.label} {col.key && getSortIcon(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ borderTop: "0" }}>
                {currentData.map((role) => (
                  <tr key={role.id} className="align-middle">
                    <td className="ps-4 py-3">
                      <div
                        className="fw-bold text-dark"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {role.title}
                      </div>
                      <div
                        className="text-primary fw-semibold"
                        style={{ fontSize: "0.65rem" }}
                      >
                        ID: {role.id}
                      </div>
                    </td>
                    <td
                      className="text-muted small"
                      style={{ fontSize: "0.8rem", maxWidth: "300px" }}
                    >
                      {role.description || "--"}
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "#334155" }}>
                      {role.reportsToId ? (
                        <span className="fw-medium">
                          {role.reportsToTitle || role.reportsTo}
                        </span>
                      ) : (
                        <span
                          className="text-muted small italic text-uppercase"
                          style={{ fontSize: "0.65rem" }}
                        >
                          Top Level
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: "0.8rem", color: "#334155" }}>
                      {role.assignmentType}
                    </td>
                    <td className="text-center">
                      <span
                        className={`badge rounded-pill ${role.isActive ? "bg-success bg-opacity-10 text-success border border-success" : "bg-danger bg-opacity-10 text-danger border border-danger"} border-opacity-25`}
                        style={{ fontSize: "0.65rem", padding: "6px 12px" }}
                      >
                        {toCamelCase(role.isActive ? "Active" : "Inactive")}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary px-3 fw-bold"
                          style={{ borderRadius: "6px", fontSize: "0.75rem" }}
                          onClick={() => handleOpenEdit(role)}
                        >
                          Edit
                        </button>
                        <button
                          className={`btn btn-sm ${role.isActive ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold`}
                          style={{
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            minWidth: "95px",
                          }}
                          onClick={() => handleStatusToggle(role)}
                        >
                          {role.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto", borderRadius: "6px" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <span className="text-muted small">
                of <strong>{totalItems}</strong> entries
              </span>
            </div>
            <nav className="d-flex align-items-center">
              <button
                className={`page-link-custom ${currentPage === 1 ? "disabled text-muted" : ""}`}
                onClick={() =>
                  currentPage > 1 && setCurrentPage(currentPage - 1)
                }
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              {totalPages > 1 && (
                <button
                  className={`page-link-custom ${currentPage === 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </button>
              )}
              {currentPage > 3 && totalPages > 5 && (
                <span className="mx-2 text-muted">...</span>
              )}
              {getPaginationGroup().map(
                (p) =>
                  p !== 1 &&
                  p !== totalPages && (
                    <button
                      key={p}
                      className={`page-link-custom ${currentPage === p ? "active" : ""}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  ),
              )}
              {currentPage < totalPages - 2 && totalPages > 5 && (
                <span className="mx-2 text-muted">...</span>
              )}
              {totalPages > 0 && (
                <button
                  className={`page-link-custom ${currentPage === totalPages ? "active" : ""}`}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </button>
              )}
              <button
                className={`page-link-custom ${currentPage === totalPages ? "disabled text-muted" : ""}`}
                onClick={() =>
                  currentPage < totalPages && setCurrentPage(currentPage + 1)
                }
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15,23,42,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 3000,
          }}
        >
          <div
            className="bg-white p-4 rounded-4 shadow-lg text-center"
            style={{ width: "400px" }}
          >
            <i
              className="bi bi-exclamation-triangle text-danger"
              style={{ fontSize: "3rem" }}
            ></i>
            <h5 className="fw-bold mt-3">Confirm Deactivation</h5>
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>{pendingRole?.title}</strong>? This may affect reporting
              lines.
            </p>
            <div className="d-flex gap-2 mt-4">
              <button
                className="btn btn-light w-100 fw-bold"
                style={{ borderRadius: "8px" }}
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger w-100 fw-bold"
                style={{ borderRadius: "8px" }}
                onClick={() => executeStatusUpdate(pendingRole)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15,23,42,0.4)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="bg-white p-4 rounded-4 shadow-lg border-0"
            style={{ width: "500px" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5
                className="fw-bold m-0 text-uppercase"
                style={{ fontSize: "1rem", letterSpacing: "1px" }}
              >
                {isEditMode ? "Edit" : "Add"} Role
              </h5>
              <button
                className="btn-close"
                onClick={() => setShowModal(false)}
              ></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12">
                  <label
                    className="small fw-bold mb-1 text-muted text-uppercase"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Role Title
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    style={{ border: "1px solid #ddd", borderRadius: "8px" }}
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                </div>
                <div className="col-12">
                  <label
                    className="small fw-bold mb-1 text-muted text-uppercase"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Reports To (Superior Role)
                  </label>
                  <select
                    className="form-select"
                    style={{ border: "1px solid #ddd", borderRadius: "8px" }}
                    value={formData.reportsToId || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        reportsToId: e.target.value || null,
                      })
                    }
                  >
                    <option value="">None (Top Level Role)</option>
                    {roles
                      .filter((r) => r.id !== formData.id)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.title}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="col-md-12">
                  <label
                    className="small fw-bold mb-1 text-muted text-uppercase"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Assignment Type
                  </label>
                  <select
                    className="form-select"
                    style={{ border: "1px solid #ddd", borderRadius: "8px" }}
                    value={formData.assignmentType}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        assignmentType: e.target.value,
                      })
                    }
                  >
                    <option value="Internal">Internal</option>
                    <option value="Deployed">Deployed</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                <div className="col-12">
                  <label
                    className="small fw-bold mb-1 text-muted text-uppercase"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    style={{ border: "1px solid #ddd", borderRadius: "8px" }}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
                <div className="col-12">
                  <div className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                    <label
                      className="form-check-label small fw-bold text-muted text-uppercase"
                      style={{ fontSize: "0.65rem" }}
                    >
                      Active Status
                    </label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-light w-100 fw-bold"
                  style={{ borderRadius: "8px" }}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary w-100 fw-bold"
                  style={{ backgroundColor: "#0f172a", borderRadius: "8px" }}
                  disabled={status === "loading"}
                >
                  {status === "loading" ? "Saving..." : "Save Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Roles;
